import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { parseCoursMarkup } from 'openlexia-markup';
import { getCourseDraft, saveCourseDraft, publishCourseDraft } from './infra/repository/CourseDraftRepository';
import CourseDraftPreview from './CourseDraftPreview';
import './course-editor.scss';

const MAX_COURSE_TITLE_LENGTH = 255;

export default function CourseDraftEditor() {
  const { draftId } = useParams();
  const navigate = useNavigate();
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [conflict, setConflict] = useState(false);
  const busy = useRef(false);
  useEffect(() => {
    let active = true;
    setDraft(null); setError(''); setStatus('');
    getCourseDraft(draftId).then(data => { if (active) setDraft(data); })
      .catch(() => { if (active) setError('Unable to load this course draft.'); });
    return () => { active = false; };
  }, [draftId]);
  const preview = useMemo(() => {
    try { return { structure: parseCoursMarkup(draft?.markup || '') }; }
    catch (error) { return { error: error.message }; }
  }, [draft?.markup]);
  const update = event => {
    const { name, value } = event.target;
    setDraft(previous => ({ ...previous, [name]: value }));
    setStatus('');
  };
  async function save(publish = false) {
    if (busy.current || !draft) return;
    busy.current = true; setError(''); setStatus(publish ? 'Publishing…' : 'Saving…');
    try {
      await saveCourseDraft(draftId, { title: draft.title, description: draft.description, markup: draft.markup });
      if (publish) {
        const result = await publishCourseDraft(draftId);
        await navigate(`/course/${result.courseId}`);
      } else { setStatus('Draft saved.'); }
    } catch (error) {
      setError(error.response?.data?.error || 'Unable to save or publish. Your changes are still available to retry.');
      setStatus('');
      setConflict(error.response?.status === 409);
    } finally { busy.current = false; }
  }
  const isBusy = status === 'Saving…' || status === 'Publishing…';
  return <main className="course-editor">
    <h1>{draft?.courseId ? 'Edit course' : 'Create course'}</h1>
    <p><Link to="/editor/course-drafts">Your course drafts</Link></p>
    {error && <p role="alert">{error}</p>}
    {conflict && draft?.courseId && <Link to={`/editor/course/${draft.courseId}/edit?fresh=true`}>Start a fresh editing draft (keeps this draft)</Link>}
    <p><output>{!draft && !error ? 'Loading course draft…' : status}</output></p>
    {draft && <>
      <fieldset className="course-editor__fields" disabled={isBusy}>
        <label htmlFor="course-title">Course title</label>
        <input id="course-title" name="title" maxLength={MAX_COURSE_TITLE_LENGTH} value={draft.title} onChange={update} />
        <label htmlFor="course-description">Description</label>
        <textarea id="course-description" name="description" value={draft.description} onChange={update} />
        <label htmlFor="course-markup">Course structure markup</label>
        <p id="course-markup-help">Use # Unit name, ## New page, or ## pageId - Existing page. Publishing creates blank pages for entries without an ID.</p>
        <textarea className="course-editor__markup" id="course-markup" name="markup" aria-describedby="course-markup-help" value={draft.markup} onChange={update} spellCheck={false} />
        <div className="course-editor__actions">
          <button type="button" onClick={() => save()}>Save course draft</button>
          <button type="button" onClick={() => save(true)}>Publish course</button>
        </div>
      </fieldset>
      <CourseDraftPreview {...preview} />
    </>}
  </main>;
}
